import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, RotateCw, X, AlertTriangle, Loader2 } from "lucide-react";
import { compressForStorage } from "../lib/utils-image";
import { useCategories } from "../lib/useCategories";

const paymentMethods = ["نقدي", "بطاقة", "تحويل بنكي", "محفظة إلكترونية", "أخرى"];

export default function ReviewInvoicePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const { categories } = useCategories();

  const [form, setForm] = useState({
    store_name: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    invoice_time: "",
    invoice_number: "",
    total_amount: 0,
    tax_amount: 0,
    discount_amount: 0,
    payment_method: "",
    category: "أخرى (تحديد)",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);

  useEffect(() => {
    if (state?.extracted) {
      setForm((prev) => ({ ...prev, ...state.extracted }));
    } else if (!state?.imageBase64 && !state?.manual) {
      // No data, redirect back
      navigate("/add");
    }
  }, []); // eslint-disable-line

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const reanalyze = async () => {
    if (!state?.imageBase64) {
      toast.error("لا توجد صورة لإعادة التحليل");
      return;
    }
    setReanalyzing(true);
    try {
      const res = await api.post("/ocr/analyze", { image_base64: state.imageBase64 });
      const data = res.data?.data || {};
      setForm((p) => ({ ...p, ...data }));
      if (res.data?.success) toast.success("تمت إعادة التحليل");
      else toast.warning("تمت إعادة التحليل بدقة منخفضة");
    } catch {
      toast.error("تعذر إعادة التحليل");
    } finally {
      setReanalyzing(false);
    }
  };

  const save = async () => {
    if (!form.total_amount || Number(form.total_amount) <= 0) {
      toast.error("مبلغ الفاتورة مطلوب ويجب أن يكون أكبر من صفر");
      return;
    }
    if (!form.invoice_date) {
      toast.error("تاريخ الفاتورة مطلوب");
      return;
    }
    setSaving(true);
    try {
      // Compress the image right before storage (after OCR is done — we keep
      // hi-res for the AI, lean copy for the database).
      let storedImage = state?.imageBase64 || "";
      if (storedImage) {
        try {
          storedImage = await compressForStorage(storedImage);
        } catch (e) {
          // If compression fails, fall back to the original (don't block save)
          // eslint-disable-next-line no-console
          console.warn("Image compression failed, storing original:", e);
        }
      }
      const payload = {
        ...form,
        total_amount: Number(form.total_amount),
        tax_amount: Number(form.tax_amount || 0),
        discount_amount: Number(form.discount_amount || 0),
        image_base64: storedImage,
      };
      await api.post("/invoices", payload);
      toast.success("تم حفظ الفاتورة بنجاح");
      navigate("/invoices");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر حفظ الفاتورة");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 fade-up max-w-6xl mx-auto" data-testid="review-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-2 font-readex">مراجعة الفاتورة</div>
        <h1 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">
          راجع البيانات قبل الحفظ
        </h1>
        <p className="text-[#5C605A] mt-2">تأكد من صحة البيانات المستخرجة، يمكنك تعديل أي حقل.</p>
      </div>

      {state?.ocrSuccess === false && (
        <div className="bg-[#C84C31]/10 border border-[#C84C31]/30 text-[#A63D26] rounded-md p-4 flex items-start gap-3" data-testid="ocr-warning">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-sm">تعذر قراءة الفاتورة بدقة، يمكنك تعديل البيانات أو إدخالها يدويًا.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Image preview */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-[#1A1C19]/10 rounded-md overflow-hidden sticky top-6">
            <div className="px-4 py-3 border-b border-[#1A1C19]/10 text-sm font-readex font-medium text-[#1A1C19]">
              صورة الفاتورة
            </div>
            <div className="bg-[#F0EFEB] flex items-center justify-center min-h-[300px]">
              {state?.previewUrl ? (
                <img src={state.previewUrl} alt="الفاتورة" className="max-h-[500px] w-auto" />
              ) : (
                <div className="text-[#8A8D86] p-10 text-center text-sm">لا توجد صورة مرفقة</div>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-3">
          <div className="bg-white border border-[#1A1C19]/10 rounded-md p-5 sm:p-6 space-y-5">
            <Field label="اسم المتجر">
              <input
                data-testid="field-store-name"
                value={form.store_name}
                onChange={(e) => update("store_name", e.target.value)}
                className="input"
                placeholder="اسم المتجر أو المنشأة"
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="التاريخ">
                <input
                  type="date"
                  data-testid="field-date"
                  value={form.invoice_date}
                  onChange={(e) => update("invoice_date", e.target.value)}
                  className="input"
                />
              </Field>
              <Field label="الوقت">
                <input
                  type="time"
                  data-testid="field-time"
                  value={form.invoice_time}
                  onChange={(e) => update("invoice_time", e.target.value)}
                  className="input"
                />
              </Field>
            </div>

            <Field label="رقم الفاتورة">
              <input
                data-testid="field-number"
                value={form.invoice_number}
                onChange={(e) => update("invoice_number", e.target.value)}
                className="input"
                placeholder="مثال: INV-1234"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="الإجمالي *">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="field-total"
                  value={form.total_amount}
                  onChange={(e) => update("total_amount", e.target.value)}
                  className="input font-mono-num"
                />
              </Field>
              <Field label="الضريبة">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="field-tax"
                  value={form.tax_amount}
                  onChange={(e) => update("tax_amount", e.target.value)}
                  className="input font-mono-num"
                />
              </Field>
              <Field label="الخصم">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="field-discount"
                  value={form.discount_amount}
                  onChange={(e) => update("discount_amount", e.target.value)}
                  className="input font-mono-num"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="طريقة الدفع">
                <select
                  data-testid="field-payment"
                  value={form.payment_method}
                  onChange={(e) => update("payment_method", e.target.value)}
                  className="input bg-white"
                >
                  <option value="">— اختر —</option>
                  {paymentMethods.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </Field>
              <Field label="الصنف">
                <select
                  data-testid="field-category"
                  value={form.category || "أخرى (تحديد)"}
                  onChange={(e) => update("category", e.target.value)}
                  className="input bg-white"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="ملاحظات">
              <textarea
                data-testid="field-notes"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                className="input min-h-[80px] resize-y"
                placeholder="ملاحظات إضافية"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <button
                onClick={save}
                disabled={saving}
                data-testid="save-btn"
                className="bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 transition-colors rounded-md px-6 py-3.5 font-medium font-readex inline-flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> : <Save className="w-5 h-5" strokeWidth={1.5} />}
                اعتماد وحفظ
              </button>

              {state?.imageBase64 && (
                <button
                  onClick={reanalyze}
                  disabled={reanalyzing}
                  data-testid="reanalyze-btn"
                  className="bg-transparent border border-[#1A1C19]/20 text-[#1A1C19] hover:border-[#1A1C19] hover:bg-black/5 transition-colors rounded-md px-6 py-3.5 font-medium font-readex inline-flex items-center justify-center gap-2"
                >
                  {reanalyzing ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> : <RotateCw className="w-5 h-5" strokeWidth={1.5} />}
                  إعادة التحليل
                </button>
              )}

              <button
                onClick={() => navigate("/add")}
                data-testid="cancel-btn"
                className="sm:col-span-2 text-[#C84C31] hover:bg-[#C84C31]/5 rounded-md px-6 py-2.5 font-medium font-readex inline-flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
                إلغاء
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: white;
          border: 1px solid rgba(26,28,25,0.15);
          border-radius: 0.375rem;
          padding: 0.75rem 1rem;
          font-size: 1rem;
          transition: all 0.2s;
        }
        .input:focus {
          outline: none;
          border-color: #1F3A33;
          box-shadow: 0 0 0 3px rgba(31,58,51,0.12);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#1A1C19] mb-2 font-readex">{label}</span>
      {children}
    </label>
  );
}
